import axios, {AxiosRequestConfig} from 'axios'
import {Dispatch, SetStateAction, useCallback, useState} from 'react'
import {JsonData} from 'utilities/types'

export interface PutDataResponse<TData = unknown> {
  loading: boolean
  error: Error | undefined
  data: TData | undefined
  setData: Dispatch<SetStateAction<TData | undefined>>
}

export interface PutDataOptions<TResponse = unknown> {
  onComplete?: (res: TResponse) => void
  onError?: (e: any) => void
  excludeAuthHeaders?: boolean
  loading?: boolean
  page?: number
  perPage?: number
}

export async function putData<
  TResponse = unknown,
  TBody extends BodyInit | undefined = BodyInit | any
>(
  path: string,
  body: TBody,
  axios_config?: string | AxiosRequestConfig
): Promise<TResponse & JsonData> {
  if (axios_config) {
    const config =
      typeof axios_config === 'string'
        ? {
            headers: {
              'x-tenant-unique-id': axios_config,
            },
          }
        : axios_config
    const res = body ? await axios.put(path, body, config) : await axios.put(path, {}, config)
    return res.data as unknown as TResponse & JsonData
  } else {
    const res = body ? await axios.put(path, body) : await axios.put(path, {})
    return res.data as unknown as TResponse & JsonData
  }
}

export default function usePutData<
  TResponse = unknown,
  TBody extends BodyInit | undefined = BodyInit
>(
  path: string,
  headerArgs?: Record<string, string>
): [
  (body: TBody | undefined, options?: PutDataOptions<TResponse>) => Promise<void>,
  PutDataResponse<TResponse>
] {
  const [data, setData] = useState<TResponse | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()

  const execPutData = useCallback(
    async (body: TBody | undefined, options?: PutDataOptions<TResponse>) => {
      setLoading(true)
      try {
        putData(path, body)
          .then((response) => {
            setLoading(false)
            setData(response as TResponse)
            options?.onComplete?.(response as TResponse)
          })
          .catch((e) => {
            setData(undefined)
            setLoading(false)
            setError(e)
            options?.onError?.(e)
          })
      } catch (e) {
        setError(e as Error)
        setLoading(false)
        options?.onError?.(e)
      }
    },
    [headerArgs, path]
  )

  return [execPutData, {data, loading, error, setData}]
}
